import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  Image as ImageIcon,
  TrendingUp,
  Eye,
  Calendar,
  Tag,
  FileText,
  CheckCircle,
  X,
  MousePointer
} from 'lucide-react';
import { fetchPersonalCampaigns, fetchReferralCampaigns, fetchGiftCards, fetchGeneralCampaigns } from '../redux/slices/campaignSlice';
import { api } from '../redux/api/axiosManager';

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaignStats, setCampaignStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Redux state
  const {
    personalCampaigns,
    referralCampaigns,
    giftCards,
    generalCampaigns,
    personalLoading,
    referralLoading,
    giftCardsLoading,
    generalLoading
  } = useSelector(state => state.campaigns);

  // Auth state'inden userId'yi al
  const { user } = useSelector(state => state.auth);

  // Kampanya tipini belirle (genel mi kişisel mi?)
  const [campaignType, setCampaignType] = useState(null);

  // Kampanya click event'ini göndermek için ref (sadece bir kez gönder)
  const clickEventSentRef = useRef(false);

  // Kampanya verilerini çek (sadece yoksa)
  useEffect(() => {
    const fetchCampaignData = async () => {
      // Eğer veri zaten varsa hiç API çağrısı yapma
      if (personalCampaigns.length > 0 || referralCampaigns.length > 0 || giftCards.length > 0 || generalCampaigns.length > 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Sadece veri yoksa API'den çek
        const promises = [];

        if (personalCampaigns.length === 0) {
          promises.push(dispatch(fetchPersonalCampaigns()));
        }
        if (referralCampaigns.length === 0) {
          promises.push(dispatch(fetchReferralCampaigns()));
        }
        if (giftCards.length === 0) {
          promises.push(dispatch(fetchGiftCards()));
        }
        if (generalCampaigns.length === 0) {
          promises.push(dispatch(fetchGeneralCampaigns()));
        }

        if (promises.length > 0) {
          await Promise.all(promises);
        }
      } catch (error) {
        // Hata sessizce yok sayılıyor
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignData();
  }, [dispatch, personalCampaigns.length, referralCampaigns.length, giftCards.length, generalCampaigns.length]);

  // Kampanya arama helper fonksiyonu
  const matchesCampaignId = (campaign, searchId) => {
    if (!campaign || !searchId) return false;
    // Tüm olası ID alanlarını kontrol et
    return campaign.id === searchId ||
      campaign.discountOfferCode === searchId ||
      campaign.attributeCode === searchId ||
      campaign.voucherCode === searchId ||
      campaign.giftItemCode === searchId ||
      String(campaign.id) === String(searchId);
  };

  // Kampanya verisini bul
  useEffect(() => {
    if (id && (personalCampaigns.length > 0 || referralCampaigns.length > 0 || giftCards.length > 0 || generalCampaigns.length > 0)) {
      // Önce genel kampanyalarda ara
      let foundCampaign = generalCampaigns.find(c => matchesCampaignId(c, id));
      if (foundCampaign) {
        // ✅ Kampanya verisi değiştiyse güncelle (Redux state güncellendiğinde)
        setCampaign(foundCampaign);
        setCampaignType('general');
        return;
      }

      // Kişisel kampanyalarda ara
      foundCampaign = personalCampaigns.find(c => matchesCampaignId(c, id));
      if (foundCampaign) {
        // ✅ Kampanya verisi değiştiyse güncelle (Redux state güncellendiğinde)
        setCampaign(foundCampaign);
        setCampaignType('personal');
        return;
      }

      // Referans çeklerinde ara
      foundCampaign = referralCampaigns.find(c => matchesCampaignId(c, id));
      if (foundCampaign) {
        // ✅ Kampanya verisi değiştiyse güncelle (Redux state güncellendiğinde)
        setCampaign(foundCampaign);
        setCampaignType('personal');
        return;
      }

      // Hediye çeklerinde ara
      foundCampaign = giftCards.find(c => matchesCampaignId(c, id));
      if (foundCampaign) {
        // ✅ Kampanya verisi değiştiyse güncelle (Redux state güncellendiğinde)
        setCampaign(foundCampaign);
        setCampaignType('personal');
        return;
      }

      // Bulunamadı - Genel kampanyalar sayfasına yönlendir
      navigate('/kampanyalar/genel');
    }
  }, [id, personalCampaigns, referralCampaigns, giftCards, generalCampaigns, navigate]);

  // Kampanya ID'sini belirleme helper fonksiyonu
  const getCampaignId = (campaign) => {
    if (!campaign) return null;
    return campaign.id ||
      campaign.discountOfferCode ||
      campaign.attributeCode ||
      campaign.voucherCode ||
      campaign.giftItemCode ||
      id;
  };

  // Kampanya detay sayfası yüklendiğinde click event'ini gönder
  useEffect(() => {
    if (campaign && !clickEventSentRef.current) {
      clickEventSentRef.current = true;

      const campaignId = getCampaignId(campaign);

      if (!campaignId) {
        return;
      }

      // Click event'ini backend'e gönder
      const sendClickEvent = async () => {
        // userId'yi belirle
        const userId = user?.id || user?.userId || user?._id || null;

        if (!userId) {
          return;
        }

        try {
          // Backend'in beklediği format - campaignId ve userId gerekli
          const requestBody = {
            campaignId: campaignId,
            userId: userId,
          };

          await api.post('/api/analytics/campaign/impression', requestBody);
        } catch (error) {
          // Hatalar sessizce yok sayılıyor
        }
      };

      sendClickEvent();
    }
  }, [campaign, campaignType, id, user]);

  // Kampanya istatistiklerini çek
  useEffect(() => {
    if (!campaign) return;

    const campaignId = getCampaignId(campaign);
    if (!campaignId) {
      return;
    }

    const fetchCampaignStats = async () => {
      try {
        setStatsLoading(true);

        // ✅ Yeni endpoint: /api/admin/analytics/campaigns/:campaignId/stats
        const response = await api.get(`/api/admin/analytics/campaigns/${campaignId}/stats`);

        if (response.data && response.data.success && response.data.data) {
          const statsData = response.data.data;
          // ✅ Yeni format: { campaignId, campaignTitle, impressions, clicks, ctr, ... }
          setCampaignStats({
            campaignId: statsData.campaignId,
            campaignTitle: statsData.campaignTitle,
            impressions: statsData.impressions ?? statsData.totalViews ?? 0,
            clicks: statsData.clicks ?? 0,
            ctr: statsData.ctr ?? null,
            uniqueUsers: statsData.uniqueUsers ?? 0,
            dailyStats: statsData.dailyStats || [],
            // Geriye dönük uyumluluk için
            totalViews: statsData.impressions ?? statsData.totalViews ?? 0,
            views: statsData.impressions ?? statsData.totalViews ?? 0,
          });
        }
      } catch (error) {
        // Hatalar sessizce yok sayılıyor
          setCampaignStats(null);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchCampaignStats();
  }, [campaign, id]);

  // Performans verileri - Backend'den gelen canlı veriler
  const performanceData = React.useMemo(() => {
    // Backend'den gelen istatistikleri kullan
    // Yeni format: { campaignId, campaignTitle, impressions, clicks, ctr, ... }
    const stats = Array.isArray(campaignStats) ? campaignStats[0] : campaignStats;

    // ✅ Görüntülenme ve tıklanma
    const impressions = stats?.impressions ?? stats?.totalViews ?? stats?.views ?? 0;
    const clicks = stats?.clicks ?? stats?.detailViews ?? 0;

    // ✅ CTR: yalnızca backend yanıtı veya clicks/impressions ile hesapla
    let ctr = '0.00';
    if (stats?.ctr !== undefined && stats?.ctr !== null) {
      ctr = parseFloat(stats.ctr).toFixed(2);
    } else if (impressions > 0) {
      ctr = ((clicks / impressions) * 100).toFixed(2);
      }

    return {
      views: impressions,
      clicks,
      ctr,
    };
  }, [campaignStats]);

  // Loading durumu - sadece gerçekten yükleniyorsa ve kampanya bulunamadıysa göster
  if (loading && !campaign && (personalCampaigns.length === 0 && referralCampaigns.length === 0 && giftCards.length === 0 && generalCampaigns.length === 0)) {
    return (
      <div className="space-y-3">
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="animate-pulse flex items-center gap-3">
            <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3">
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="animate-pulse space-y-3">
                <div>
                  <div className="h-4 bg-gray-200 rounded w-1/5 mb-3"></div>
                  <div className="h-12 bg-gray-200 rounded-xl w-full"></div>
                </div>
                <div>
                  <div className="h-4 bg-gray-200 rounded w-1/6 mb-3"></div>
                  <div className="h-24 bg-gray-200 rounded-xl w-full"></div>
                </div>
                <div className="h-64 bg-gray-200 rounded-xl w-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Kampanya bulunamadı
  if (!campaign) {
    return (
      <div className="space-y-3">
        <div className="bg-white rounded-xl shadow-sm p-4 text-center border border-gray-100">
          <div className="flex flex-col items-center gap-3">
            <div className="p-4 bg-red-50 rounded-full">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Kampanya Bulunamadı</h1>
              <p className="text-gray-500 mt-2">Aradığınız kampanya mevcut değil veya kaldırılmış olabilir.</p>
            </div>
            <button
              onClick={() => navigate(campaignType === 'general' ? '/kampanyalar/genel' : '/kampanyalar/bana-ozel')}
              className="mt-3 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-medium flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" /> Geri Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(campaignType === 'general' ? '/kampanyalar/genel' : '/kampanyalar/bana-ozel')}
              className="p-3 hover:bg-gray-50 rounded-xl transition-all border border-gray-100 hover:border-gray-200 text-gray-500 hover:text-gray-900 group"
            >
              <ArrowLeft className="w-6 h-6 group-hover:-translate-x-1 transition-transform" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Kampanya Detayı</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Campaign Details */}
        <div className="lg:col-span-2 space-y-3">
          {/* Kampanya Bilgileri */}
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Kampanya Bilgileri</h2>
            </div>

            <div className="space-y-3">
              {/* Title */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Kampanya Başlığı</label>
                <div className="w-full px-5 py-4 border border-gray-200 rounded-xl bg-gray-50 text-lg font-medium text-gray-900">
                  {campaign.title || '-'}
                </div>
              </div>

              {/* Kampanya Eski İsmi */}
              {campaign.discountOfferDescription && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Kampanya Eski İsmi</label>
                  <div className="w-full px-5 py-4 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-600">
                    {campaign.discountOfferDescription}
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Detaylı Açıklama</label>
                <div className="w-full px-5 py-4 border border-gray-200 rounded-xl bg-gray-50 min-h-[120px] text-gray-700 leading-relaxed">
                  {campaign.campaignContent || campaign.description || '-'}
                </div>
              </div>

              {/* Short Description */}
              {campaign.campaignContentShort && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Kısa Açıklama</label>
                  <div className="w-full px-5 py-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-700">
                    {campaign.campaignContentShort}
                  </div>
                </div>
              )}

              {/* Features */}
              {campaign.campaignFeatures && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Kampanya Özellikleri</label>
                  <div className="w-full px-5 py-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-700">
                    {campaign.campaignFeatures}
                  </div>
                </div>
              )}

              {/* Terms and Conditions */}
              {campaign.campaignTermsAndConditions && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Şart ve Koşullar</label>
                  <div className="w-full px-5 py-4 border border-gray-200 rounded-xl bg-gray-50 min-h-[100px] text-gray-700 text-sm space-y-2">
                    {campaign.campaignTermsAndConditions
                      .split(/\n|#/)
                      .map((item) => item.trim())
                      .filter(Boolean)
                      .map((term, index) => (
                        <div key={index} className="flex items-start gap-2.5">
                          <span className="block w-1.5 h-1.5 rounded-full bg-gray-500 mt-2 flex-shrink-0" />
                          <p className="flex-1">{term}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Image */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Mobil App Görseli</label>
                <div className="relative rounded-xl overflow-hidden border border-gray-100 shadow-sm group">
                  {campaign.image || campaign.campaignImage ? (
                    <>
                      <img
                        src={campaign.image || campaign.campaignImage}
                        alt={campaign.title}
                        className="w-full h-80 object-cover transform group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </>
                  ) : (
                    <div className="w-full h-64 bg-gray-50 flex items-center justify-center">
                      <div className="text-center text-gray-400">
                        <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="font-medium">Görsel bulunamadı</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Kampanya Performansı */}
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-3">
                <div className="p-2 bg-indigo-50 rounded-xl">
                  <TrendingUp className="text-indigo-600 w-6 h-6" />
                </div>
                Kampanya Performansı
              </h2>
              {statsLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                  <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full"></div>
                  Yükleniyor...
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl border border-blue-100 transition-transform hover:scale-[1.02]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-white/60 rounded-lg">
                    <Eye className="text-blue-600 w-5 h-5" />
                  </div>
                  <p className="text-sm text-blue-800 font-bold uppercase tracking-wide opacity-70">Görüntülenme</p>
                </div>
                {statsLoading ? (
                  <div className="h-8 w-20 bg-blue-100 rounded animate-pulse"></div>
                ) : (
                  <p className="text-2xl font-black text-blue-900 tracking-tight">{performanceData.views.toLocaleString('tr-TR')}</p>
                )}
              </div>

              <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-100 transition-transform hover:scale-[1.02]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-white/60 rounded-lg">
                    <MousePointer className="text-emerald-600 w-5 h-5" />
                  </div>
                  <p className="text-sm text-emerald-800 font-bold uppercase tracking-wide opacity-70">Tıklanma</p>
                </div>
                {statsLoading ? (
                  <div className="h-8 w-20 bg-emerald-100 rounded animate-pulse"></div>
                ) : (
                  <p className="text-2xl font-black text-emerald-900 tracking-tight">{performanceData.clicks.toLocaleString('tr-TR')}</p>
                )}
              </div>

              <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl border border-amber-100 transition-transform hover:scale-[1.02]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-white/60 rounded-lg">
                    <TrendingUp className="text-amber-600 w-5 h-5" />
                  </div>
                  <p className="text-sm text-amber-800 font-bold uppercase tracking-wide opacity-70">CTR</p>
                </div>
                {statsLoading ? (
                  <div className="h-8 w-20 bg-amber-100 rounded animate-pulse"></div>
                ) : (
                  <p className="text-2xl font-black text-amber-900 tracking-tight">%{performanceData.ctr}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Quick Stats */}
        <div className="space-y-3">
          {/* Status Card */}
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <h3 className="text-base font-bold text-gray-900 mb-6 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-gray-400" />
              Durum
            </h3>

            <div className={`p-4 rounded-xl text-center border-2 transition-all ${(campaign.isActiveApp === 1 || campaign.status === 'active')
              ? 'bg-green-50 border-green-200'
              : 'bg-gray-50 border-gray-200'
              }`}>
              <p className="text-sm font-bold text-gray-500 mb-2 uppercase tracking-wide">Kampanya Durumu</p>
              <div className="flex items-center justify-center gap-2">
                {(campaign.isActiveApp === 1 || campaign.status === 'active') ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <X className="w-6 h-6 text-gray-400" />
                )}
                <p className={`text-2xl font-black ${(campaign.isActiveApp === 1 || campaign.status === 'active')
                  ? 'text-green-700'
                  : 'text-gray-700'
                  }`}>
                  {(campaign.isActiveApp === 1 || campaign.status === 'active') ? 'Yayında' : 'Yayında Değil'}
                </p>
              </div>
            </div>
          </div>

          {/* Date Info */}
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <h3 className="text-base font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              Tarih Bilgileri
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                <span className="text-sm font-medium text-gray-500">Başlangıç</span>
                <span className="font-bold text-gray-900">
                  {campaign.startDate && campaign.startDate !== '-' ?
                    campaign.startDate :
                    'Belirtilmemiş'
                  }
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                <span className="text-sm font-medium text-gray-500">Bitiş</span>
                <span className="font-bold text-gray-900">
                  {campaign.endDate && campaign.endDate !== '-' ?
                    campaign.endDate :
                    'Belirtilmemiş'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Campaign Info */}
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <h3 className="text-base font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Tag className="w-5 h-5 text-gray-400" />
              Teknik Detaylar
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0 last:pb-0">
                <span className="text-sm font-medium text-gray-500">Kampanya ID</span>
                <span className="font-mono text-xs font-bold bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg border border-blue-100">
                  {campaign.id || 'N/A'}
                </span>
              </div>
              {campaign.attributeCode && (
                <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0 last:pb-0">
                  <span className="text-sm font-medium text-gray-500">Attribute Code</span>
                  <span className="font-mono text-xs font-bold bg-purple-50 text-purple-700 px-3 py-1.5 rounded-lg border border-purple-100">
                    {campaign.attributeCode}
                  </span>
                </div>
              )}
              {campaign.discountOfferCode && (
                <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0 last:pb-0">
                  <span className="text-sm font-medium text-gray-500">Discount Code</span>
                  <span className="font-mono text-xs font-bold bg-amber-50 text-amber-700 px-3 py-1.5 rounded-lg border border-amber-100">
                    {campaign.discountOfferCode}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetail;

